import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, increment, deleteDoc, runTransaction, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useRoles } from '../context/RolesContext';
import { useAppConfig } from '../context/AppConfigContext';
import { isAdminEmail } from '../utils/admin';

export default function Thread() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const { adminEmails } = useAppConfig();
  const { isMod } = useRoles();
  const isAdmin = isAdminEmail(user?.email, adminEmails);
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [myVote, setMyVote] = useState(0); // -1, 0, 1
  const [voting, setVoting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'threads', threadId);
    const unsubThread = onSnapshot(
      ref,
      (snap) => {
        setThread({ id: snap.id, ...snap.data() });
      },
      (err) => {
        console.error('Failed to load thread:', err);
        setError(err.message || 'Failed to load thread');
      }
    );
    const q = query(collection(db, 'threads', threadId, 'comments'), orderBy('createdAt', 'asc'));
    const unsubComments = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('Failed to load comments:', err);
        setError(err.message || 'Failed to load comments');
      }
    );
    return () => { unsubThread(); unsubComments(); };
  }, [threadId]);

  // Load current user's vote for this thread
  useEffect(() => {
    if (!user) { setMyVote(0); return; }
    const voteRef = doc(db, 'threads', threadId, 'votes', user.uid);
    const unsub = onSnapshot(voteRef, (snap) => {
      const v = snap.exists() ? snap.data().value : 0;
      setMyVote(typeof v === 'number' ? v : 0);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, user]);

  // Upsert vote and adjust score atomically; clicking same vote toggles to neutral (0)
  const voteThread = async (requestedValue) => {
    try {
      if (!user) { setError('Log in to vote'); return; }
      if (voting) return;
      setVoting(true);
      const threadRef = doc(db, 'threads', threadId);
      const voteRef = doc(db, 'threads', threadId, 'votes', user.uid);
      await runTransaction(db, async (tx) => {
        const [threadSnap, voteSnap] = await Promise.all([tx.get(threadRef), tx.get(voteRef)]);
        if (!threadSnap.exists()) throw new Error('Thread missing');
        const oldVal = voteSnap.exists() ? (voteSnap.data().value || 0) : 0;
        const newVal = (requestedValue === oldVal) ? 0 : requestedValue; // toggle if same
        const delta = newVal - oldVal;
        if (delta !== 0) tx.update(threadRef, { score: increment(delta) });
        tx.set(voteRef, { value: newVal, updatedAt: serverTimestamp() }, { merge: true });
      });
    } catch (err) {
      console.error('Vote failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to vote'}`);
    } finally {
      setVoting(false);
    }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (thread?.locked) {
      setError('Thread is locked');
      return;
    }
    if (!text.trim()) return;
    setPosting(true);
    setError('');
    try {
      await addDoc(collection(db, 'threads', threadId, 'comments'), {
        text: text.trim(),
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorPhotoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'threads', threadId), {
        replies: increment(1),
        lastActivityAt: serverTimestamp(),
      });
      setText('');
    } catch (err) {
      console.error('Post comment failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to post comment'}`);
    } finally {
      setPosting(false);
    }
  };

  const togglePin = async () => {
    try {
      await updateDoc(doc(db, 'threads', threadId), { pinned: !thread.pinned });
    } catch (err) {
      console.error('Toggle pin failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to toggle pin'}`);
    }
  };

  const toggleLock = async () => {
    try {
      await updateDoc(doc(db, 'threads', threadId), { locked: !thread.locked });
    } catch (err) {
      console.error('Toggle lock failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to toggle lock'}`);
    }
  };

  const removeThread = async () => {
    try {
      if (!window.confirm('Delete this thread? This will remove all comments and votes.')) return;
      setDeleting(true);
      const threadRef = doc(db, 'threads', threadId);
      // Delete comments
      const commentsSnap = await getDocs(collection(db, 'threads', threadId, 'comments'));
      await Promise.all(commentsSnap.docs.map((d) => deleteDoc(d.ref)));
      // Delete votes
      const votesSnap = await getDocs(collection(db, 'threads', threadId, 'votes'));
      await Promise.all(votesSnap.docs.map((d) => deleteDoc(d.ref)));
      // Delete the thread itself
      await deleteDoc(threadRef);
    } catch (err) {
      console.error('Delete thread failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to delete thread'}`);
    } finally {
      setDeleting(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!isAdmin) return;
    try {
      if (!window.confirm('Delete this comment?')) return;
      const commentRef = doc(db, 'threads', threadId, 'comments', commentId);
      await deleteDoc(commentRef);
      // Best-effort adjust reply count
      await updateDoc(doc(db, 'threads', threadId), {
        replies: increment(-1),
        lastActivityAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Delete comment failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to delete comment'}`);
    }
  };

  if (!thread) {
    return <div style={{ paddingTop: '6rem', textAlign: 'center' }}>Loading…</div>;
  }

  const canDelete = isAdmin;

  return (
    <div className="page-section">
      <div className="page-container thread-container">
        <div className="thread-back">
          <Link to="/forums" className="link-accent">← Back to Forums</Link>
        </div>
        <div className="glass-card thread-header" style={{ padding: 16, marginBottom: 16 }}>
          <h2 className="card-title" style={{ marginTop: 0, textAlign: 'center' }}>{thread.title}</h2>
          <div className="meta-text thread-meta" style={{ textAlign: 'center' }}>by {thread.authorName} {thread.locked ? '· locked' : ''} {thread.pinned ? '· pinned' : ''}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
            {isMod && (
              <>
                <button className="nav-link" onClick={togglePin}>{thread.pinned ? 'Unpin' : 'Pin'}</button>
                <button className="nav-link" onClick={toggleLock}>{thread.locked ? 'Unlock' : 'Lock'}</button>
              </>
            )}
            {canDelete && (
              <button className="btn-glass-danger" onClick={removeThread} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button
              className={`btn-glass-secondary btn-vote-up ${myVote === 1 ? 'active' : ''}`}
              onClick={() => voteThread(1)}
              disabled={!user || voting}
              title={user ? (myVote === 1 ? 'Remove upvote' : 'Upvote') : 'Log in to vote'}
              aria-pressed={myVote === 1}
            >
              ▲
            </button>
            <div style={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}>{typeof thread.score === 'number' ? thread.score : 0}</div>
            <button
              className={`btn-glass-secondary btn-vote-down ${myVote === -1 ? 'active' : ''}`}
              onClick={() => voteThread(-1)}
              disabled={!user || voting}
              title={user ? (myVote === -1 ? 'Remove downvote' : 'Downvote') : 'Log in to vote'}
              aria-pressed={myVote === -1}
            >
              ▼
            </button>
          </div>
        </div>

        <div className="glass-card thread-comments" style={{ padding: 0, marginBottom: 16 }}>
          {comments.length === 0 ? (
            <div className="meta-text" style={{ padding: 16, textAlign: 'center' }}>No comments yet.</div>
          ) : (
            <ul className="thread-comment-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {comments.map((c) => (
                <li key={c.id} className="thread-comment-item">
                  <div className="thread-comment-row">
                    {(() => {
                      let localPreview = null;
                      try { if (user && user.uid === c.authorId) localPreview = localStorage.getItem(`LOCAL_AVATAR_${user.uid}`); } catch (_) {}
                      const avatarUrl = c.authorPhotoURL || ((user && user.uid === c.authorId) ? (user.photoURL || localPreview || null) : null);
                      return avatarUrl ? (
                        <img src={avatarUrl} alt="avatar" className="forum-avatar thread-avatar" />
                      ) : (
                        <div className="forum-avatar forum-avatar-fallback thread-avatar" />
                      );
                    })()}
                    <div>
                      <div className="meta-text" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span>{c.authorName}</span>
                        {isAdmin ? (
                          <button className="nav-link" type="button" onClick={() => removeComment(c.id)}>
                            Delete
                          </button>
                        ) : null}
                      </div>
                      <div className="thread-comment-text">{c.text}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {user ? (
          <form onSubmit={postComment} className="glass-card form-glass" style={{ padding: 16 }}>
            {error && <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{error}</div>}
            <textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a reply…"
              className="textarea-glass"
            />
            <div className="form-row" style={{ marginTop: 8 }}>
              <button disabled={posting || !text.trim() || thread?.locked} type="submit" className="btn-glass-primary btn-block">
                {posting ? 'Posting…' : 'Post reply'}
              </button>
            </div>
          </form>
        ) : (
          <div className="glass-card" style={{ padding: 16, textAlign: 'center' }}>Log in to reply.</div>
        )}
      </div>
    </div>
  );
}
